import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { Controller, useController } from 'react-hook-form';
import { UploadFile } from '@mui/icons-material';
import { useApiUrl, useNotification, useTranslate } from '@refinedev/core';
import { ConsentUploaderProps } from '@/features/studies/types/study-edit.interface';
import logger from '@/core/services';
import { axiosInstance } from '@/core/services';
import { useTranslation } from 'react-i18next';

export const ConsentUploader: React.FC<ConsentUploaderProps> = ({
  control,
  errors,
  disabled,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const apiUrl = useApiUrl();
  const { open: openNotification } = useNotification();
  const translate = useTranslate();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    onChangeUrl: (value: string) => void,
    onChangeName: (value: string) => void,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post(
        `${apiUrl}/admin/studies/upload-consent`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      onChangeUrl(response.data.url);
      onChangeName(file.name);
      openNotification?.({
        type: 'success',
        message: translate('notifications.success'),
        description: translate('notifications.consentPdfUploaded'),
      });
    } catch (error) {
      logger.error('Error uploading consent PDF:', error);
      openNotification?.({
        type: 'error',
        message: translate('notifications.error'),
        description: translate(
          'notifications.errorUploadingConsentPdf',
          'Failed to upload consent PDF.',
        ),
      });
    } finally {
      setUploading(false);
    }
  };

  const consentPdfNameController = useController({
    control,
    name: 'consentPdfName',
  });

  return (
    <Box>
      <Controller
        name="consentPdfUrl"
        control={control}
        render={({ field }) => (
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={
                uploading ? <CircularProgress size={20} /> : <UploadFile />
              }
              disabled={disabled || uploading}
              fullWidth
            >
              {uploading
                ? t('pages.studies.actions.uploading', 'Uploading...')
                : consentPdfNameController.field.value ||
                  t('pages.studies.actions.uploadConsentPdf')}
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={(e) =>
                  handleFileUpload(
                    e,
                    field.onChange,
                    consentPdfNameController.field.onChange,
                  )
                }
                disabled={disabled || uploading}
              />
            </Button>
            {errors.consentPdfUrl && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {errors.consentPdfUrl.message as string}
              </Typography>
            )}
          </Box>
        )}
      />
    </Box>
  );
};
